/**
 * What a page shows in the common frame (proposal §7): its title, used by the
 * tab of the page, and its commands, shown in the command bar. Pages declare
 * them with usePageTitle() and useCommands().
 */
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { IconName } from '../Icon.js';

export interface Command {
  key: string;
  label: string;
  icon: IconName;
  onClick: () => void;
  /** The main action of the page, highlighted. */
  primary?: boolean;
  disabled?: boolean;
}

interface PageChrome {
  title: string | null;
  commands: Command[];
  setTitle: (title: string | null) => void;
  setCommands: (commands: Command[]) => void;
}

const PageChromeContext = createContext<PageChrome | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);
  const [commands, setCommands] = useState<Command[]>([]);
  const value = useMemo(() => ({ title, commands, setTitle, setCommands }), [title, commands]);
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export function usePageChrome(): PageChrome {
  const chrome = useContext(PageChromeContext);
  if (!chrome) throw new Error('usePageChrome used outside PageChromeProvider');
  return chrome;
}

/** Title of the page in its tab, e.g. the name of the record it shows. */
export function usePageTitle(title: string | null): void {
  const { setTitle } = usePageChrome();
  useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
}

/**
 * Commands of the page in the command bar. The bar is updated only when a
 * label, icon or state changes; the latest handlers are always the ones called.
 */
export function useCommands(commands: Command[]): void {
  const { setCommands } = usePageChrome();
  const latest = useRef(commands);
  latest.current = commands;
  const signature = commands
    .map(
      (command) =>
        `${command.key}:${command.label}:${command.icon}:${command.primary}:${command.disabled}`,
    )
    .join('|');

  useEffect(() => {
    setCommands(
      latest.current.map((command) => ({
        ...command,
        onClick: () => latest.current.find((item) => item.key === command.key)?.onClick(),
      })),
    );
    return () => setCommands([]);
  }, [signature, setCommands]);
}
